import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import {
  FaUsers,
  FaMap,
  FaChevronRight,
  FaEllipsisV,
  FaEnvelope,
  FaCog,
  FaCrosshairs,
  FaTree,
} from 'react-icons/fa';
import { YButton } from '../../../ui/YButton';
import { ClubMiniMap } from './ClubMiniMap';
import {
  clubHasMapBoundary,
  formatClubMetaLine,
  getClubRoleKey,
  getClubRoleLabel,
  getStandCount,
} from '../clubsDisplayUtils';

export function ClubCard({
  club,
  onEnter,
  onViewMap,
  onManageMembers,
  onSettings,
  onPostAnnouncement,
}) {
  const roleKey = getClubRoleKey(club);
  const roleLabel = getClubRoleLabel(club);
  const standCount = getStandCount(club);
  const hasBoundary = clubHasMapBoundary(club);
  const metaLine = formatClubMetaLine(club);
  const indexLabel = String(club.id).padStart(2, '0');

  return (
    <article className="club-card" data-testid={`club-card-${club.id}`}>
      <span className="club-card__index" aria-hidden="true">
        {indexLabel}
      </span>

      <div className="club-card__map-wrap">
        <ClubMiniMap
          clubId={club.id}
          acreage={club.acreage}
          standCount={standCount}
          hasBoundary={hasBoundary}
        />
      </div>

      <div className="club-card__body">
        <div className="club-card__header">
          <div className="club-card__heading">
            {roleLabel ? (
              <div className="club-card__badges">
                <span className={`club-card__role club-card__role--${roleKey}`}>{roleLabel}</span>
              </div>
            ) : null}
            <h2 className="club-card__title">{club.name}</h2>
            {metaLine ? <p className="club-card__meta">{metaLine}</p> : null}
          </div>

          <Dropdown align="end" className="club-card__menu">
            <Dropdown.Toggle
              variant="link"
              className="club-card__menu-btn"
              aria-label={`Actions for ${club.name}`}
              id={`club-menu-${club.id}`}
            >
              <FaEllipsisV aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="club-card__menu-panel">
              {hasBoundary ? (
                <Dropdown.Item onClick={() => onViewMap(club)}>
                  <FaMap className="me-2" aria-hidden="true" />
                  View Club Map
                </Dropdown.Item>
              ) : null}
              {club.is_president ? (
                <>
                  <Dropdown.Item onClick={() => onSettings(club)}>
                    <FaCog className="me-2" aria-hidden="true" />
                    Club Settings
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onPostAnnouncement(club)}>
                    <FaEnvelope className="me-2" aria-hidden="true" />
                    Post Announcement
                  </Dropdown.Item>
                </>
              ) : null}
              <Dropdown.Item onClick={() => onManageMembers(club)}>
                <FaUsers className="me-2" aria-hidden="true" />
                Manage Members
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {club.description ? <p className="club-card__description">{club.description}</p> : null}

        <div className="club-card__stats" role="group" aria-label="Club summary">
          <div className="club-card__stat">
            <span className="club-card__stat-label">
              <FaUsers aria-hidden="true" />
              Members
            </span>
            <span className="club-card__stat-value">{club.member_count ?? 0}</span>
          </div>
          <div className="club-card__stat">
            <span className="club-card__stat-label">
              <FaCrosshairs aria-hidden="true" />
              Stands
            </span>
            <span className="club-card__stat-value">{standCount}</span>
          </div>
          <div className="club-card__stat">
            <span className="club-card__stat-label">
              <FaTree aria-hidden="true" />
              Acres
            </span>
            <span className="club-card__stat-value">
              {club.acreage != null && !Number.isNaN(Number(club.acreage))
                ? Number(club.acreage).toLocaleString()
                : '—'}
            </span>
          </div>
        </div>

        <div className="club-card__actions">
          {hasBoundary ? (
            <YButton
              type="button"
              variant="secondary"
              size="sm"
              className="club-card__action-map"
              onClick={() => onViewMap(club)}
              aria-label="View Club Map"
            >
              <FaMap aria-hidden="true" />
              Club Map
            </YButton>
          ) : null}
          <YButton
            type="button"
            size="sm"
            className="club-card__action-enter"
            onClick={() => onEnter(club)}
          >
            Enter Club
            <FaChevronRight aria-hidden="true" />
          </YButton>
        </div>
      </div>
    </article>
  );
}

ClubCard.propTypes = {
  club: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    member_count: PropTypes.number,
    acreage: PropTypes.number,
    established_year: PropTypes.number,
    created: PropTypes.string,
    is_president: PropTypes.bool,
    current_user_role: PropTypes.string,
    hunt_locations: PropTypes.arrayOf(PropTypes.object),
    map: PropTypes.object,
  }).isRequired,
  onEnter: PropTypes.func.isRequired,
  onViewMap: PropTypes.func.isRequired,
  onManageMembers: PropTypes.func.isRequired,
  onSettings: PropTypes.func.isRequired,
  onPostAnnouncement: PropTypes.func.isRequired,
};
